import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Container
} from '@mui/material';
import { SalesPartnerEarningModel } from 'src/models/salesPartner/SalesPartnerEarningModel';
import SalesPartnerService from 'src/services/salesPartner/salesPartnerServices';
import { VemigoDateRangeField } from 'src/components/VemigoGrid/VemigoDateRangeField';
import { useSalesPartnerData } from '../../SalesPartnerData';
import VemigoGrid from 'src/components/VemigoGrid';
import { AppDownloadsModel } from 'src/models/salesPartner/SalesPartnerAppDownloadModel';
import { PendingInquiriesModel } from 'src/models/salesPartner/SalesPartnerInquiries';

// Icons
const Icons = {
  earnings: '💰',
  downloads: '📥',
  services: '📊',
  inquiries: '❓',
};

interface DashboardData {
  totalEarnings: string;
  appDownloads: string;
  totalServices: string;
  openInquiries: string;
  appDownloadsChange: string;
  totalServicesChange: string;
  openInquiriesChange: string;
}

interface ReportData {
  earnings: SalesPartnerEarningModel | null;
  downloads: AppDownloadsModel[];
  inquiries: PendingInquiriesModel[];
}

type ActiveTable = 'earnings' | 'downloads' | 'inquiries' | null;

const Report = () => {
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [reportData, setReportData] = useState<ReportData>({
    earnings: null,
    downloads: [],
    inquiries: []
  });
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    totalEarnings: '0',
    appDownloads: '0',
    totalServices: '0',
    openInquiries: '0',
    appDownloadsChange: '+0%',
    totalServicesChange: '+0%',
    openInquiriesChange: '+0%',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTable, setActiveTable] = useState<ActiveTable>(null);

  const { salesPartner } = useSalesPartnerData();

  const getCurrentMonthDateRange = useCallback((): string[] => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatDate = (date: Date): string => {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    return [formatDate(firstDay), formatDate(lastDay)];
  }, []);

  useEffect(() => {
    const currentMonthRange = getCurrentMonthDateRange();
    setSelectedDates(currentMonthRange);
  }, [getCurrentMonthDateRange]);

  const convertToBackendFormat = useCallback((dateString: string): string => {
    const [day, month, year] = dateString.split('/');
    return `${month}/${day}/${year}`;
  }, []);

  const formatCurrency = useCallback((amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, []);

  const formatDate = useCallback((dateString: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN');
    } catch {
      return dateString;
    }
  }, []);

  const fetchReportData = useCallback(async () => {
    if (selectedDates.length !== 2 || !salesPartner?.id) return;

    setLoading(true);
    setError(null);
    setActiveTable(null);

    try {
      const [startDate, endDate] = selectedDates;
      const formattedStartDate = convertToBackendFormat(startDate);
      const formattedEndDate = convertToBackendFormat(endDate);

      const results = await Promise.allSettled([
        SalesPartnerService.earningData(salesPartner.id, formattedStartDate, formattedEndDate),
        SalesPartnerService.getMyAppDownloadsData(salesPartner.id, formattedStartDate, formattedEndDate),
        SalesPartnerService.GetPendingInquiries(salesPartner.id)
      ]);

      const earningsData =
        results[0].status === "fulfilled"
          ? results[0].value
          : { totalEarning: 0, totalServices: 0, services: [] };

      const downloadsData =
        results[1].status === "fulfilled" ? results[1].value : [];

      const inquiriesData =
        results[2].status === "fulfilled" ? results[2].value : [];

      setReportData({
        earnings: earningsData,
        downloads: Array.isArray(downloadsData) ? downloadsData : [],
        inquiries: Array.isArray(inquiriesData) ? inquiriesData : []
      });

      setDashboardData({
        totalEarnings: parseFloat(
          Number(earningsData.totalEarning ?? 0).toFixed(2)
        ).toString(),

        appDownloads: Array.isArray(downloadsData) ? downloadsData.length.toString() : '0',
        totalServices: earningsData.totalServices != null ? earningsData.totalServices.toString() : '0',
        openInquiries: Array.isArray(inquiriesData) ? inquiriesData.length.toString() : '0',

        appDownloadsChange: '+0%',
        totalServicesChange: '+0%',
        openInquiriesChange: '+0%',
      });

    } catch (error) {
      console.error("Unexpected Error:", error);
      setError("Unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, [selectedDates, salesPartner, convertToBackendFormat]);



  const handleGoClick = () => {
    if (selectedDates.length === 2) {
      fetchReportData();
    }
  };


  const isGoButtonDisabled = selectedDates.length !== 2 || loading;

  const getEarningsTableData = useCallback(() => {
    if (!reportData.earnings?.services) return [];
    return reportData.earnings.services.map((service, index) => ({
      id: `earning-${index}-${service.serviceId}-${Date.now()}`,
      serviceDate: formatDate(service.serviceDate),
      customerName: service.customerName || '-',
      customerCar: service.customerCar || '-',
      serviceName: service.serviceName || '-',
      basePrice: service.basePrice || 0,
      earningPrice: service.earningPrice || 0,
    }));
  }, [reportData.earnings, formatDate]);

  const getDownloadsTableData = useCallback(() => {
    if (!reportData.downloads) return [];
    return reportData.downloads.map((download, index) => ({
      id: `download-${index}-${download.mobileNumber}`,
      customerFirstName: download.customerFirstName || '-',
      customerLastName: download.customerLastName || '-',
      mobileNumber: download.mobileNumber || '-',
      contactNumber1: download.contactNumber1 || '-',
      createdDateTime: formatDate(download.createdDateTime),
      lastServiceOrderDate: formatDate(download.lastServiceOrderDate),
    }));
  }, [reportData.downloads, formatDate]);

  const getInquiriesTableData = useCallback(() => {
    if (!reportData.inquiries) return [];
    return reportData.inquiries.map((inquiry) => ({
      id: inquiry.id,
      customerName: inquiry.customerName || `${inquiry.customerFirstName || ''} ${inquiry.customerLastName || ''}`.trim() || '-',
      mobileNumber: inquiry.mobileNumber || '-',
      emailId: inquiry.emailId || '-',
      note: inquiry.note || '-',
      car: inquiry.car || '-',
      cityName: inquiry.cityName || '-',
      areaName: inquiry.areaName || '-',
      status: inquiry.status || 'Pending',
    }));
  }, [reportData.inquiries]);

  const earningsTableColumns = [
    { field: 'serviceDate', headerName: 'Service Date', width: 150, flex: 1 },
    { field: 'customerName', headerName: 'Customer Name', width: 200, flex: 1 },
    { field: 'customerCar', headerName: 'Customer Car', width: 150, flex: 1 },
    { field: 'serviceName', headerName: 'Service Name', width: 200, flex: 1 },
    {
      field: 'basePrice',
      headerName: 'Base Price',
      width: 150,
      flex: 1,
      renderCell: (params: any) => formatCurrency(params.value)
    },
    {
      field: 'earningPrice',
      headerName: 'Earning Price',
      width: 150,
      flex: 1,
      renderCell: (params: any) => formatCurrency(params.value)
    },
  ];

  const downloadsTableColumns = [
    { field: 'customerFirstName', headerName: 'First Name', width: 150, flex: 1 },
    { field: 'customerLastName', headerName: 'Last Name', width: 150, flex: 1 },
    { field: 'mobileNumber', headerName: 'Mobile Number', width: 150, flex: 1 },
    { field: 'contactNumber1', headerName: 'Contact Number', width: 150, flex: 1 },
    { field: 'createdDateTime', headerName: 'Created Date', width: 150, flex: 1 },
    { field: 'lastServiceOrderDate', headerName: 'Last Service Date', width: 150, flex: 1 },
  ];

  const inquiriesTableColumns = [
    { field: 'customerName', headerName: 'Customer Name', width: 200, flex: 1 },
    { field: 'mobileNumber', headerName: 'Mobile Number', width: 150, flex: 1 },
    { field: 'emailId', headerName: 'Email', width: 200, flex: 1 },
    { field: 'note', headerName: 'Note', width: 250, flex: 1 },
    { field: 'car', headerName: 'Car', width: 150, flex: 1 },
    { field: 'cityName', headerName: 'City', width: 150, flex: 1 },
    { field: 'areaName', headerName: 'Area', width: 150, flex: 1 },
    { field: 'status', headerName: 'Status', width: 120, flex: 1 },
  ];

  const getTableData = () => {
    switch (activeTable) {
      case 'earnings': return getEarningsTableData();
      case 'downloads': return getDownloadsTableData();
      case 'inquiries': return getInquiriesTableData();
      default: return [];
    }
  };

  const getTableColumns = () => {
    switch (activeTable) {
      case 'earnings': return earningsTableColumns;
      case 'downloads': return downloadsTableColumns;
      case 'inquiries': return inquiriesTableColumns;
      default: return [];
    }
  };

  const getTableTitle = () => {
    switch (activeTable) {
      case 'earnings': return 'Earnings & Services Details';
      case 'downloads': return 'App Downloads Details';
      case 'inquiries': return 'Pending Inquiries Details';
      default: return '';
    }
  };


  const StatCard = ({
    title,
    value,
    change,
    isEarnings = false,
    color = '#FFF4DE',
    icon,
    tableType
  }: {
    title: string;
    value: string;
    change?: string;
    isEarnings?: boolean;
    color?: string;
    icon?: string;
    tableType: ActiveTable;
  }) => (
    <Card
      sx={{
        borderRadius: 3,
        padding: 3,
        width: '100%',
        marginBottom: 2,
        backgroundColor: color,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: activeTable === tableType ? '2px solid #1976d2' : '2px solid transparent',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }
      }}
      onClick={() => setActiveTable(activeTable === tableType ? null : tableType)}
    >
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Typography variant="h6" sx={{ fontSize: '15px', color: '#718096', marginBottom: 1.5, fontWeight: '500' }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: change ? 1 : 0 }}>
          {icon && (
            <Typography sx={{ fontSize: '28px', marginRight: 1.5 }}>
              {icon}
            </Typography>
          )}
          <Typography variant="h4" sx={{ fontSize: '26px', fontWeight: '700', color: isEarnings ? '#2F855A' : '#2D3748' }}>
            {isEarnings ? formatCurrency(parseFloat(value)) : value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg">
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} md={8}>
          <Typography variant="h4" gutterBottom component='h1'>
            Date
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            <VemigoDateRangeField
              width="250px"
              selectedSEDates={selectedDates}
              setSelectedSEDates={(dates) => {
                setSelectedDates(dates);
                if (dates.length === 2) setShowDatePicker(false);
              }}
              show={showDatePicker}
              setShow={setShowDatePicker}
              mode="range"
            />
            <Button
              variant="contained"
              onClick={handleGoClick}
              disabled={isGoButtonDisabled}
              sx={{
                minHeight: '56px',
                px: 4,
                fontWeight: 'bold',
                borderRadius: 2
              }}
            >
              Apply
            </Button>
          </Box>
        </Grid>
      </Grid>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading report data...
            </Typography>
          </Box>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!loading && reportData.earnings && (
        <Grid container spacing={3} sx={{ marginY: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Total Earnings" value={dashboardData.totalEarnings} change={dashboardData.appDownloadsChange} isEarnings={true} color="#FFF4DE" icon={Icons.earnings} tableType="earnings" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="App Downloads" value={dashboardData.appDownloads} change={dashboardData.appDownloadsChange} color="#FFF4DE" icon={Icons.downloads} tableType="downloads" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Total Services" value={dashboardData.totalServices} change={dashboardData.totalServicesChange} color="#FFF4DE" icon={Icons.services} tableType="earnings" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Open Inquiries" value={dashboardData.openInquiries} change={dashboardData.openInquiriesChange} color="#FFF4DE" icon={Icons.inquiries} tableType="inquiries" />
          </Grid>
        </Grid>
      )}

      {activeTable && !loading && getTableData().length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            {getTableTitle()} ({getTableData().length} records)
          </Typography>
          <VemigoGrid rows={getTableData()} columns={getTableColumns()} />
        </Box>
      )}

      {activeTable && !loading && getTableData().length === 0 && (
        <Box sx={{ mt: 4, textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No data available for {getTableTitle().toLowerCase()}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default Report;
